import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BasePage from '../../components/Layout/BasePage';
import MasterDetailLayout from '../../components/Layout/MasterDetailLayout';
import { renderTabLabel } from '../../components/Layout/tabLabel';
import useUrlTab from '../../hooks/useUrlTab';
import Quotations from '../Quotes/Quotations';
import SalesOverview from './SalesOverview';
import Customers from '../Customers/Customers';
import SalesOrders from './SalesOrders';
import SalesInvoices from './SalesInvoices';

function SalesPage({ initialDetailTarget, onDetailConsumed }) {
  const { t } = useTranslation('sales');
  const location = useLocation();
  const navigate = useNavigate();

  // Active tab in the URL (?tab=), remembered per module
  const { activeTab, setActiveTab } = useUrlTab(
    'sales', ['overview', 'quotations', 'orders', 'invoices', 'customers'], 'overview'
  );
  const [quotationsToolbar, setQuotationsToolbar] = useState(null);
  const [salesOrdersToolbar, setSalesOrdersToolbar] = useState(null);
  const [invoicesToolbar, setInvoicesToolbar] = useState(null);
  const [initialOrderId, setInitialOrderId] = useState(null);
  const [initialQuoteId, setInitialQuoteId] = useState(null);
  const [initialInvoiceId, setInitialInvoiceId] = useState(null);
  const [initialCustomerId, setInitialCustomerId] = useState(null);
  // ?newQuote=true&productId=N (product wizard): open a new quotation with that product
  const [newQuoteRequest, setNewQuoteRequest] = useState(null);

  // Handle detail target from App.js (cross-module navigation)
  useEffect(() => {
    if (!initialDetailTarget) return;

    if (initialDetailTarget.type === 'salesOrder') {
      setActiveTab('orders');
      setInitialOrderId(initialDetailTarget.id);
      setTimeout(() => setInitialOrderId(null), 200);
    } else if (initialDetailTarget.type === 'salesOrdersList') {
      setActiveTab('orders');
    } else if (initialDetailTarget.type === 'quote') {
      setActiveTab('quotations');
      setInitialQuoteId(initialDetailTarget.id);
      setTimeout(() => setInitialQuoteId(null), 200);
    } else if (initialDetailTarget.type === 'invoice') {
      setActiveTab('invoices');
      setInitialInvoiceId(initialDetailTarget.id);
      setTimeout(() => setInitialInvoiceId(null), 200);
    } else if (initialDetailTarget.type === 'customer') {
      setActiveTab('customers');
      setInitialCustomerId(initialDetailTarget.id);
      setTimeout(() => setInitialCustomerId(null), 200);
    }

    if (onDetailConsumed) onDetailConsumed();
  }, [initialDetailTarget]);

  // Product → "Create Quotation" opens a new quote here (?newQuote=true&productId=…)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('newQuote') === 'true') {
      setNewQuoteRequest({ productId: urlParams.get('productId') });
      // Consume the request so a reload or tab switch doesn't open another new quote
      navigate('/sales?tab=quotations', { replace: true });
    }
  }, [location.search]);

  const tabs = [
    {
      id: 'overview',
      label: renderTabLabel(t('tabs.overview'), 'o'),
      shortcut: 'o',
      hasSubTabs: false
    },
    {
      id: 'quotations',
      label: renderTabLabel(t('tabs.quotations'), 'q'),
      shortcut: 'q',
      hasSubTabs: false
    },
    {
      id: 'orders',
      label: renderTabLabel(t('tabs.orders'), 'd'),
      shortcut: 'd',
      hasSubTabs: false
    },
    {
      id: 'invoices',
      label: renderTabLabel(t('tabs.invoices'), 'i'),
      shortcut: 'i',
      hasSubTabs: false
    },
    {
      id: 'customers',
      label: renderTabLabel(t('tabs.customers'), 'u'),
      shortcut: 'u',
      hasSubTabs: false
    }
  ];





  // Expose toolbar setters for different tabs
  React.useEffect(() => {
    if (activeTab === 'quotations') {
      window.setQuotationsToolbar = setQuotationsToolbar;
    } else if (activeTab === 'orders') {
      window.setSalesOrdersToolbar = setSalesOrdersToolbar;
    } else if (activeTab === 'invoices') {
      window.setInvoicesToolbar = setInvoicesToolbar;
    }
    return () => {
      if (window.setQuotationsToolbar) {
        delete window.setQuotationsToolbar;
      }
      if (window.setSalesOrdersToolbar) {
        delete window.setSalesOrdersToolbar;
      }
      if (window.setInvoicesToolbar) {
        delete window.setInvoicesToolbar;
      }
    };
  }, [activeTab]);


  // Configuration object for MasterDetailLayout. Searching is done in each list's own search box
  // (the tab bar used to show a second one that did nothing)
  const config = {
    tabs,
    entities: {},
    toolbars: {
      overview: null,
      quotations: quotationsToolbar,
      orders: salesOrdersToolbar,
      invoices: invoicesToolbar,
      customers: null
    }
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="tab-content with-padding">
            <SalesOverview />
          </div>
        );

      case 'orders':
        return <SalesOrders initialOrderId={initialOrderId} />;

      case 'invoices':
        return <SalesInvoices initialInvoiceId={initialInvoiceId} />;

      case 'quotations':
        return <Quotations initialQuoteId={initialQuoteId} newQuoteRequest={newQuoteRequest} />;

      case 'customers':
        return <Customers initialCustomerId={initialCustomerId} />;

      default:
        return (
          <div className="tab-content">
            <div className="empty-state">
              <h3>{t('defaultContent.moduleTitle')}</h3>
              <p>{t('defaultContent.underDevelopment')}</p>
            </div>
          </div>
        );
    }
  };

  return (
    <BasePage title={t('pageTitle')} className="sales-page">
      <MasterDetailLayout
        config={config}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      >
        {renderTabContent()}
      </MasterDetailLayout>
    </BasePage>
  );
}

export default SalesPage;